import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Tag, Input, Select, Space, Typography, Card, Button,
  Popconfirm, Modal, Form, message, Tooltip,
} from 'antd';
import {
  SearchOutlined, DeleteOutlined, PlusOutlined, StopOutlined,
} from '@ant-design/icons';
import { theme as antdTheme } from 'antd';
import axios from 'axios';

const { Title, Text } = Typography;
const { Option } = Select;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const TYPE_COLOR = { unsubscribe: 'orange', bounce: 'red', complaint: 'volcano' };

const AdminSuppression = () => {
  const { token: tk } = antdTheme.useToken();
  const [form]        = Form.useForm();

  const [data, setData]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);
  const [search, setSearch]   = useState('');
  const [typeF, setTypeF]     = useState('');
  const [orgFilter, setOrgF]  = useState('');
  const [orgs, setOrgs]       = useState([]);
  const [addModal, setAddModal] = useState(false);
  const [adding, setAdding]     = useState(false);

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 100 };
      if (search)    params.search = search;
      if (typeF)     params.type   = typeF;
      if (orgFilter) params.org_id = orgFilter;
      const res = await axios.get('/api/admin/suppression', { headers: authHeader(), params });
      setData(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search, typeF, orgFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get('/api/admin/orgs', { headers: authHeader() })
      .then(r => setOrgs(r.data)).catch(() => {});
  }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/admin/suppression/${id}`, { headers: authHeader() });
      message.success('Entry removed');
      load();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to remove');
    }
  };

  const handleAdd = async () => {
    try {
      const values = await form.validateFields();
      setAdding(true);
      await axios.post('/api/admin/suppression', values, { headers: authHeader() });
      message.success('Email suppressed');
      setAddModal(false);
      form.resetFields();
      load();
    } catch (err) {
      if (err.response) message.error(err.response.data?.message || 'Failed to add');
    } finally {
      setAdding(false);
    }
  };

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: v => <Text copyable style={{ fontSize: 13 }}>{v}</Text>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: t => <Tag color={TYPE_COLOR[t] || 'default'}>{t}</Tag>,
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      render: v => <Text type="secondary" style={{ fontSize: 12 }}>{v || '—'}</Text>,
    },
    {
      title: 'Organisation',
      dataIndex: 'org_name',
      key: 'org',
      width: 180,
      render: v => <Text style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 140,
      render: d => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, r) => (
        <Popconfirm
          title="Remove suppression?"
          description="This email will be able to receive campaigns again."
          onConfirm={() => handleDelete(r.id)}
          okText="Remove" okButtonProps={{ danger: true }}
        >
          <Tooltip title="Remove suppression">
            <Button size="small" icon={<DeleteOutlined />} danger type="text" />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: '0 2px' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={4} style={{ marginBottom: 4 }}>Suppression List Admin</Title>
          <Text type="secondary">View and manage suppressed emails across all organisations.</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModal(true)} style={{ borderRadius: 8 }}>
          Add Suppression
        </Button>
      </div>

      <Card
        style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}` }}
        styles={{ body: { padding: 0 } }}
      >
        {/* Toolbar */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '14px 20px', borderBottom: `1px solid ${tk.colorBorderSecondary}`,
        }}>
          <Input
            placeholder="Search email…"
            prefix={<SearchOutlined style={{ color: tk.colorTextQuaternary }} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            allowClear
            style={{ width: 220, borderRadius: 8 }}
          />
          <Select
            value={typeF || 'all'}
            onChange={v => setTypeF(v === 'all' ? '' : v)}
            style={{ width: 140 }}
          >
            <Option value="all">All Types</Option>
            <Option value="unsubscribe">Unsubscribe</Option>
            <Option value="bounce">Bounce</Option>
            <Option value="complaint">Complaint</Option>
          </Select>
          <Select
            value={orgFilter || 'all'}
            onChange={v => setOrgF(v === 'all' ? '' : v)}
            style={{ width: 200 }}
            showSearch
            optionFilterProp="children"
          >
            <Option value="all">All Organisations</Option>
            {orgs.map(o => <Option key={o.id} value={String(o.id)}>{o.name}</Option>)}
          </Select>
          <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 12 }}>
            {total.toLocaleString()} entries
          </Text>
        </div>

        <Table
          rowKey="id"
          dataSource={data}
          columns={columns}
          loading={loading}
          size="small"
          pagination={{
            current: page,
            pageSize: 100,
            total,
            onChange: p => { setPage(p); load(p); },
            showTotal: n => `${n} entries`,
            style: { padding: '12px 20px', margin: 0 },
          }}
          style={{ borderRadius: '0 0 12px 12px', overflow: 'hidden' }}
        scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* Add suppression modal */}
      <Modal
        open={addModal}
        title={<Space><StopOutlined style={{ color: '#ef4444' }} /> Add Suppression</Space>}
        onCancel={() => { setAddModal(false); form.resetFields(); }}
        onOk={handleAdd}
        confirmLoading={adding}
        okText="Suppress Email"
        okButtonProps={{ danger: true }}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Enter a valid email' },
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>
          <Form.Item name="type" label="Type" initialValue="unsubscribe">
            <Select>
              <Option value="unsubscribe">Unsubscribe</Option>
              <Option value="bounce">Bounce</Option>
              <Option value="complaint">Complaint</Option>
            </Select>
          </Form.Item>
          <Form.Item name="org_id" label="Organisation (optional)">
            <Select allowClear placeholder="Leave blank to suppress globally">
              {orgs.map(o => <Option key={o.id} value={o.id}>{o.name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="reason" label="Reason (optional)">
            <Input placeholder="e.g. Manual admin action" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminSuppression;
